/**
 * Preference extraction utilities
 * Copied from FundingKit for reuse across persona-aware contexts
 */

export type Preference = {
  id: string | number;
  name: string;
  type: string;
  source: "user" | "company";
  filterKey: string;
};

/**
 * Get filter key for different preference types
 * @param type - Preference type (sectors, countries, etc.)
 * @returns Elasticsearch filter key
 */
const getFilterKey = (type: string): string => {
  switch (type) {
    case "sectors":
      return "sectors.name.keyword";
    case "sub_sectors":
      return "sub_sectors.name.keyword";
    case "countries":
      return "ops_countries.name.keyword";
    case "agbase_categories":
      return "agbase_category.name.keyword";
    case "agbase_value_chains":
      return "agbase_value_chain.name.keyword";
    default:
      return `${type}.name.keyword`;
  }
};

/**
 * Extract preferences from user metadata
 * @param userMetadata - User metadata object with preference arrays
 * @param source - Source of preferences ('user' or 'company')
 * @returns Array of preference objects
 */
export function extractPreferences(
  userMetadata: any,
  source: "user" | "company" = "user",
): Preference[] {
  const preferences: Preference[] = [];

  if (!userMetadata || typeof userMetadata !== "object") {
    return preferences;
  }

  // Iterate through all properties of userMetadata
  Object.keys(userMetadata).forEach((key) => {
    const value = userMetadata[key];

    // Check if the value is an array and contains items with id and name
    if (Array.isArray(value) && value.length > 0) {
      value.forEach((item) => {
        if (item && typeof item === "object" && item.id && item.name) {
          // Remove "preferred_" prefix from key if it exists
          const type = key.startsWith("preferred_")
            ? key.replace("preferred_", "")
            : key;

          preferences.push({
            id: item.id,
            name: item.name,
            type: type,
            source: source,
            filterKey: getFilterKey(type),
          });
        }
      });
    }
  });

  return preferences;
}

/**
 * Extract and merge preferences from both user metadata and company/organization data
 * @param userMetadata - User metadata object with preference arrays
 * @param companyData - Company/organization data object
 * @returns Merged array of preference objects (user + company, no duplicates)
 */
export function extractMergedPreferences(
  userMetadata: any,
  companyData: any,
): Preference[] {
  // Start with user preferences
  const userPreferences = extractPreferences(userMetadata, "user");

  // Extract company data and convert to preference format
  const companyPreferences: Preference[] = [];

  if (!companyData) {
    return userPreferences;
  }

  // Add sectors (note: sectors use 'value' instead of 'name')
  if (companyData?.sectors && Array.isArray(companyData.sectors)) {
    companyData.sectors.forEach((sector: any) => {
      if (sector?.id && sector?.value) {
        companyPreferences.push({
          id: sector.id,
          name: sector.value,
          type: "sectors",
          source: "company",
          filterKey: getFilterKey("sectors"),
        });
      }
    });
  }

  // Add sub_sectors (products)
  if (companyData?.sub_sectors && Array.isArray(companyData.sub_sectors)) {
    companyData.sub_sectors.forEach((subSector: any) => {
      if (subSector?.id && subSector?.value) {
        companyPreferences.push({
          id: subSector.id,
          name: subSector.value,
          type: "sub_sectors",
          source: "company",
          filterKey: "sub_sectors.name.keyword",
        });
      }
    });
  }

  // Add sectors/sub_sectors from investment targets
  if (companyData?.investments && Array.isArray(companyData.investments)) {
    companyData.investments.forEach((investment: any) => {
      const target = investment?.target;
      if (!target) return;
      (target.sectors ?? []).forEach((sector: any) => {
        if (sector?.id && sector?.name) {
          companyPreferences.push({
            id: sector.id,
            name: sector.name,
            type: "sectors",
            source: "company",
            filterKey: getFilterKey("sectors"),
          });
        }
      });
      (target.sub_sectors ?? []).forEach((sub: any) => {
        if (sub?.id && sub?.name) {
          companyPreferences.push({
            id: sub.id,
            name: sub.name,
            type: "sub_sectors",
            source: "company",
            filterKey: "sub_sectors.name.keyword",
          });
        }
      });
    });
  }

  // Add operating countries
  if (companyData?.ops_countries && Array.isArray(companyData.ops_countries)) {
    companyData.ops_countries.forEach((country: any) => {
      if (country?.id && country?.name) {
        companyPreferences.push({
          id: country.id,
          name: country.name,
          type: "countries",
          source: "company",
          filterKey: getFilterKey("countries"),
        });
      }
    });
  }

  // Add agbase_category (if exists and different from sub_sectors)
  if (
    companyData?.agbase_category &&
    Array.isArray(companyData.agbase_category)
  ) {
    companyData.agbase_category.forEach((category: any) => {
      if (category?.id && category?.name) {
        companyPreferences.push({
          id: category.id,
          name: category.name,
          type: "agbase_categories",
          source: "company",
          filterKey: getFilterKey("agbase_categories"),
        });
      }
    });
  }

  // Add agbase_value_chain
  if (
    companyData?.agbase_value_chain &&
    Array.isArray(companyData.agbase_value_chain)
  ) {
    companyData.agbase_value_chain.forEach((valueChain: any) => {
      if (valueChain?.id && valueChain?.name) {
        companyPreferences.push({
          id: valueChain.id,
          name: valueChain.name,
          type: "agbase_value_chains",
          source: "company",
          filterKey: getFilterKey("agbase_value_chains"),
        });
      }
    });
  }

  // Merge preferences, avoiding duplicates based on id and type
  const mergedPreferences = [...userPreferences];

  companyPreferences.forEach((companyPref) => {
    const exists = mergedPreferences.some(
      (userPref) =>
        userPref.id === companyPref.id && userPref.type === companyPref.type,
    );

    if (!exists) {
      mergedPreferences.push(companyPref);
    }
  });

  return mergedPreferences;
}
